import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useSubscription } from '@apollo/client';
import PropTypes from 'prop-types';
import { FEATURE_FLAG_UPDATED, ENABLED_FEATURE_FLAGS } from '../graphql/modules/feature-flag';

export const FeatureFlagContext = React.createContext({});

const MAX_FF_REFETCH_DELAY_IN_SECONDS = 10;
const generateRandomDelayInSeconds = (max = MAX_FF_REFETCH_DELAY_IN_SECONDS) => {
  return Math.floor(Math.random() * max) + 1;
};

const FFPASS_PARAM_NAME = 'ffpass';

export const FeatureFlagProvider = ({ user, children }) => {
  const [isFirstFetch, setIsFirstFetch] = useState(true);
  const [enabledFeatureFlagsMap, setEnabledFeatureFlagsMap] = useState({});
  const [getEnabledFeatureFlags, { loading }] = useLazyQuery(ENABLED_FEATURE_FLAGS);
  const { data: featureFlagUpdated } = useSubscription(FEATURE_FLAG_UPDATED);
  const fetchAndStoreEnabledFeatureFlags = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const ffpass = urlParams.get(FFPASS_PARAM_NAME);
    const { data } = await getEnabledFeatureFlags({
      variables: {
        input: {
          ...(ffpass && { ffpass }),
        },
      },
    });

    const enabledFeatureFlags = data?.enabledFeatureFlags ?? [];
    const map = enabledFeatureFlags.reduce((acc, ff) => {
      acc[ff] = true;

      return acc;
    }, {});

    setEnabledFeatureFlagsMap(map);
    return enabledFeatureFlagsMap;
  };

  useEffect(() => {
    const delayInMs = (isFirstFetch ? 0 : generateRandomDelayInSeconds()) * 1000;
    setTimeout(() => {
      fetchAndStoreEnabledFeatureFlags();
      setIsFirstFetch(false);
    }, delayInMs);
  }, [user, featureFlagUpdated]);

  const value = useMemo(() => {
    return {
      enabledFeatureFlagsMap,
      loading,
    };
  }, [enabledFeatureFlagsMap]);

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};

FeatureFlagProvider.propTypes = {
  isLoading: PropTypes.bool,
  user: PropTypes.object,
  children: PropTypes.node.isRequired,
};

export const useEnabledFeatureFlags = () => {
  return useContext(FeatureFlagContext);
};
